import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fromLonLat, toLonLat } from 'ol/proj';
import { XYZ } from 'ol/source';
import { MapProvider } from 'contexts/MapContext';
import { Loading, SearchInput } from 'components';
import { useDebounce } from 'hooks/useDebounce';
import nominationAPI from 'services/api/nominatimAPI';
import baseMaps from './baseMaps';
import { MapComponent } from './components/MapComponent';
import { MapLayer } from './components/Layers/MapLayer';
import { SideBar } from './components/SideBar';
import { ModalLayerOptions } from './components/ModalLayerOptions';
import * as Styled from './styles';
import { MapModal } from './components/MapModal';
import { useFilter } from 'hooks/useFilter';
import maps from './components/MapModal/maps';
import { clearText } from 'utils/clearText';
import { findAllThemeLayers, findCategorias, findLayersAttrubute } from 'services/themeRequest';
import { ICategory, ILayers } from 'contexts/@types/Layers';
import { useTranslation } from 'react-i18next';
import { useLayers } from 'hooks/useLayers';
import { useMap } from 'hooks/useMap';
import { TabelaAtributos } from './components/TabelaAtributos';
import { toast } from 'react-toastify';
import { ZIndexProvider } from 'contexts/ZIndexContext';
import { api } from 'services/api/api';
import { clearStoredPreferences } from 'contexts/AuthContext';
import { initializeMonitoringSession } from 'services/monitoring';
import { useLocation } from 'react-router-dom';
import { applyOpenLayerPreferences, buildSearchResultCategories, getAllCategoryLayers, LayerPreference, parsePreference, sameLayerPreference } from 'utils/mapPreferences';

interface IOptions {
  address: {
    city: string;
    state: string;
    country: string;
    town: string;
    municipality: string;
  };
  lat: string;
  lon: string;
}

const MapComponentMemoized = memo(MapComponent);
const MapLayerMemoized = memo(MapLayer);

function getZoom() {
  if (window.innerWidth <= 500) {
    return 3;
  }
  if (window.innerWidth <= 800) {
    return 3.5;
  }
  return 4.5;
}

function getCenter() {
  if (window.innerWidth <= 500) {
    return [-63, -35];
  }
  if (window.innerWidth <= 800) {
    return [-65, -20];
  }
  return [-58, -16];
}


function getInitialMapView() {
  const saved = parsePreference<{ center?: number[]; zoom?: number }>('mapView', {});
  const center = Array.isArray(saved.center) && saved.center.length === 2 ? saved.center : getCenter();
  const zoom = typeof saved.zoom === 'number' ? saved.zoom : getZoom();

  return { center, zoom };
}

function getInitialBaseMap() {
  const saved = parsePreference<{ index?: number }>('basemap', {});
  return typeof saved.index === 'number' && baseMaps[saved.index] ? saved.index : 0;
}
function TabelaAtributosContainer() {
  const { layersActived } = useLayers();
  const { addedLayers } = useMap();

  if (layersActived.length === 0 && addedLayers.length === 0) return null;

  return <TabelaAtributos />;
}


function MapPreferencesBridge({ baseMapCounter }: { baseMapCounter: number }) {
  const { map, handleAddAllOverlayLayers } = useMap();
  const { layersActived } = useLayers();
  const location = useLocation();
  const didSyncBaseMap = useRef(false);

  const buildPayload = useCallback(() => {
    const center = map?.getView().getCenter();
    const zoom = map?.getView().getZoom();
    const mapView = center && typeof zoom === 'number'
      ? { center: toLonLat(center), zoom }
      : parsePreference('mapView', {});

    localStorage.setItem('mapView', JSON.stringify(mapView));
    localStorage.setItem('basemap', JSON.stringify({ index: baseMapCounter }));

    return {
      activeLayers: parsePreference('activeLayers', []),
      activeCategories: parsePreference('activeCategories', {}),
      searchResultLayers: parsePreference('searchResultLayers', []),
      basemap: { index: baseMapCounter },
      mapView,
    };
  }, [baseMapCounter, map]);

  const savePreferences = useCallback((useBeacon = false) => {
    const payload = buildPayload();

    if (useBeacon && navigator.sendBeacon) {
      const baseURL = api.defaults.baseURL || '';
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${baseURL}/auth/preferences/`, blob);
      return;
    }

    window.dispatchEvent(new CustomEvent('preferencesUpdated'));
  }, [buildPayload]);

  useEffect(() => {
    if (!didSyncBaseMap.current) {
      didSyncBaseMap.current = true;
      return;
    }

    localStorage.setItem('basemap', JSON.stringify({ index: baseMapCounter }));
  }, [baseMapCounter]);

  useEffect(() => {
    if (!map || !layersActived.length) return;
    handleAddAllOverlayLayers(layersActived);
  }, [handleAddAllOverlayLayers, layersActived, map]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (window.__isAuth) {
        savePreferences(true);
      } else {
        clearStoredPreferences();
      }
    };
    const handleExplicitSave = () => {
      if (window.__isAuth) {
        savePreferences(false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('p3m:saveMapPreferences', handleExplicitSave);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('p3m:saveMapPreferences', handleExplicitSave);
    };
  }, [savePreferences, location.pathname]);

  return null;
}
export function Map() {
  const [isSearchActived, setIsSearchActived] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<IOptions[]>([]);
  const [baseMapCounter, setBaseMapCounter] = useState<number>(getInitialBaseMap());
  const [isBaseMapModalOpen, setIsBaseMapModalOpen] = useState<boolean>(false);
  const initialMapView = useMemo(() => getInitialMapView(), []);
  const [center, setCenter] = useState<number[]>(initialMapView.center);
  const [zoom, setZoom] = useState<number>(initialMapView.zoom);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const { clearFilterData } = useFilter();
  const centerMemo = useMemo(() => fromLonLat(center), [center]);
  const sourceMemo = useMemo(() => new XYZ(baseMaps[baseMapCounter]), [baseMapCounter]);
  const { i18n } = useTranslation();
  const { setCategoryList, setLayerList, setLayerAttributeList, handleCountLayersActived, handleEnableOnlyTheseLayers } = useLayers();
  const baseCategoriesRef = useRef<ICategory[]>([]);

  useEffect(() => {
    void initializeMonitoringSession();
  }, []);

  const applySavedViewportPreferences = useCallback(() => {
    const savedBaseMap = getInitialBaseMap();
    const savedMapView = getInitialMapView();

    setBaseMapCounter(savedBaseMap);
    setCenter(savedMapView.center);
    setZoom(savedMapView.zoom);
  }, []);

  const applySavedMapPreferences = useCallback((baseCategories: ICategory[]) => {
    const baseLayers = getAllCategoryLayers(baseCategories);
    const savedSearchResults = parsePreference('searchResultLayers', []);
    const pesquisas = buildSearchResultCategories(savedSearchResults, baseLayers);

    if (pesquisas.length) {
      sessionStorage.setItem('pesquisas', JSON.stringify(pesquisas));
    }

    const openedPreferences = parsePreference('activeCategories', { categories: [], themes: [], subthemes: [] });
    const categoriesWithPreferences = applyOpenLayerPreferences([...baseCategories, ...pesquisas], openedPreferences);
    const allLayers = getAllCategoryLayers(categoriesWithPreferences);
    const activeLayerPreferences = parsePreference<LayerPreference[]>('activeLayers', []);
    const activeLayers = activeLayerPreferences
      .map(preference => allLayers.find(layer => sameLayerPreference(layer, preference)))
      .filter((layer): layer is ILayers => !!layer);

    window.__mapPreferencesApplied = true;
    setCategoryList(categoriesWithPreferences);
    handleEnableOnlyTheseLayers(activeLayers);
  }, [handleEnableOnlyTheseLayers, setCategoryList]);
  
  async function loadInitialData() {
    try {
      setIsLoading(true);

      const cachedPesquisas = sessionStorage.getItem('pesquisas');

      const resp = await findCategorias<ICategory[]>(i18n.language);

      const data = resp.data

      const commoditiesIndex = data.findIndex(c => c.id_categoria === 1);
      const commodities = data[commoditiesIndex];

      if (commodities) {
        for (const tema of commodities.temas) {
          tema.layers?.forEach(layer => {
            layer.layer_alias = `${layer.layertitle} - ${tema.nm_tema}`;
          });

          tema.subtema?.forEach(subtema =>
            subtema.layers?.forEach(layer => {
              layer.layer_alias = `${layer.layertitle} - ${subtema.nm_subtema}`;
            })
          );
        }

        data[commoditiesIndex] = commodities;
      }

      sessionStorage.setItem('categorias', JSON.stringify(data));

      const [themeLayersResp, layerAttributesResp] = await Promise.all([
        findAllThemeLayers<ILayers[]>(i18n.language),
        findLayersAttrubute<ILayers[]>(i18n.language),
      ]);

      const baseCategories = data;
      const cachedSearchCategories = cachedPesquisas ? JSON.parse(cachedPesquisas) : [];

      baseCategoriesRef.current = baseCategories;
      setCategoryList([...baseCategories, ...cachedSearchCategories]);
      setLayerList(themeLayersResp.data);
      setLayerAttributeList(layerAttributesResp.data);
      applySavedViewportPreferences();
      applySavedMapPreferences(baseCategories);
      handleCountLayersActived();

    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      toast.error('Erro ao carregar dados do mapa.');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const handlePreferencesLoaded = () => {
      applySavedViewportPreferences();
      if (!baseCategoriesRef.current.length) return;
      applySavedMapPreferences(baseCategoriesRef.current);
    };

    window.addEventListener('preferencesLoaded', handlePreferencesLoaded);
    return () => window.removeEventListener('preferencesLoaded', handlePreferencesLoaded);
  }, [applySavedMapPreferences, applySavedViewportPreferences]);

  useEffect(() => {
    const handleClearDisplay = () => {
      if (baseCategoriesRef.current.length) {
        baseCategoriesRef.current = baseCategoriesRef.current.map(category => ({
          ...category,
          opened: false,
          qtdOpenend: 0,
          temas: category.temas.map(theme => ({
            ...theme,
            opened: false,
            qtdOpenend: 0,
            subtema: theme.subtema?.map(subtheme => ({
              ...subtheme,
              opened: false,
              qtdOpenend: 0,
            })),
          })),
        }));
      }
      setCenter(initialMapView.center);
      setZoom(initialMapView.zoom);
      setBaseMapCounter(0);
    };

    window.addEventListener('p3m:clearMapDisplay', handleClearDisplay);
    return () => {
      window.removeEventListener('p3m:clearMapDisplay', handleClearDisplay);
    };
  }, [initialMapView]);

  const handleChangeSearchInput = (value: string) => {
    setSearchValue(value);
  };

  const handleEnterSubmit = async (key: string) => {
    if (key === 'Enter') {
      setCenter([Number(searchOptions[0].lon), Number(searchOptions[0].lat)]);
      setZoom(10);
    }
  };

  const handleOnSelectedOption = (option: IOptions) => {
    setCenter([Number(option.lon), Number(option.lat)]);
    setZoom(10);
  };

  function verifySearchResults(data: IOptions, query: string) {
    return (
      clearText(data.address.city)?.includes(clearText(query)) ||
      clearText(data.address.town)?.includes(clearText(query)) ||
      clearText(data.address.municipality)?.includes(clearText(query)) ||
      clearText(data.address.state)?.includes(clearText(query)) ||
      clearText(data.address.country)?.includes(clearText(query))
    );
  }

  useEffect(() => {
    (async () => {
      if (debouncedSearchValue.startsWith('-')) {
        const terms = debouncedSearchValue.split(',');
        if (Number(terms[0]) < 0 && Number(terms[1]) < 7) {
          if (terms[0] && terms[0].trim().length > 0 && terms[1] && terms[1].trim().length > 0) {
            const { data } = await nominationAPI.get(
              `/reverse?lat=${Number(terms[1])}&lon=${Number(terms[0])}&format=json&format=json`,
            );
            if (data.error != 'Unable to geocode') {
              const point = [
                {
                  address: {
                    city: data.address.city ? data.address.city : data.address.city_district,
                    state: data.address.state,
                    country: data.address.country,
                    town: data.address.town,
                    municipality: data.address.municipality,
                  },
                  lat: terms[1],
                  lon: terms[0],
                },
              ];
              setSearchOptions(point);
            }
          }
        }
      } else {
        if (!debouncedSearchValue || debouncedSearchValue.trim().length === 0) return;
        const { data } = await nominationAPI.get(`/search?&addressdetails=1&q=${debouncedSearchValue}&format=json`);
        const dataFilter = data.filter(
          (item: IOptions) => item.lat && item.lon && verifySearchResults(item, debouncedSearchValue),
        );
        setSearchOptions(dataFilter.slice(0, 5));
      }
    })();
  }, [debouncedSearchValue]);

  useEffect(() => {
    clearFilterData();
  }, []);

  return (
    <ZIndexProvider>
      <MapProvider>
        <MapPreferencesBridge baseMapCounter={baseMapCounter} />
        {isLoading && <Loading />}
        <Styled.Wrapper>
          <SideBar
            handleSearchInputActive={() => setIsSearchActived(!isSearchActived)}
            onBaseMapModalOpen={() => setIsBaseMapModalOpen(!isBaseMapModalOpen)}
          />
          {isSearchActived && (
            <SearchInput
              options={searchOptions}
              value={searchValue}
              onChange={({ target }) => handleChangeSearchInput(target.value)}
              onKeyDown={({ key }) => handleEnterSubmit(key)}
              onSelected={option => handleOnSelectedOption(option)}
              autoComplete="off"
            />
          )}
          <MapComponentMemoized center={centerMemo} zoom={zoom}>
            <MapLayerMemoized source={sourceMemo} zIndex={0} />
          </MapComponentMemoized>
          {isBaseMapModalOpen && (
            <MapModal setIsOpen={setIsBaseMapModalOpen} onChangeBaseMap={index => setBaseMapCounter(index)} />
          )}
        </Styled.Wrapper>

        <ModalLayerOptions />

        <Styled.MapDisclaimer>
          <span>
            Mapa:
            <a className='notranslate' href={maps[baseMapCounter].href} target="_blank" rel="noreferrer">
              {maps[baseMapCounter].organization}
            </a>
          </span>
        </Styled.MapDisclaimer>
        <TabelaAtributosContainer />
      </MapProvider>
    </ZIndexProvider>
  );
}
